import { type ApolloClient } from '@apollo/client';

import { DraftFragment } from '@graphql/platform';

import { CheckoutType } from '@root/components/checkout/types';
import { parseDraftToValues } from '@root/components/checkout/utilities/drafts';
import type { InitialValues } from '@root/components/checkout/utilities/initial_values/types';
import {
  restoreStorageSessionCheckoutData,
  withChallengerStorageDefaults,
} from '@root/components/checkout/utilities/persistence';

import { deferPromise } from '@utils/async';

import {
  ReonboardingOptions,
  getReonboardingInitialValues,
} from './initial_values/reonboarding';
import {
  ShareableOptions,
  getShareableInitialValues,
} from './initial_values/shareable';

// prettier-ignore
type ValueInitializerOptions = (
  | ({ type: CheckoutType.Standard } & {
      draft?: DraftFragment;
      applyChallengerDefaults?: boolean;
    })
  | ({ type: CheckoutType.Shareable } & ShareableOptions)
  | ({ type: CheckoutType.Agent } & ShareableOptions)
  | ({ type: CheckoutType.Reonboarding } & ReonboardingOptions)
);

// If the user navigates to another page and then navigates back, we want to use
// the persisted session storage values instead of refetching from the API
let hasFetchedReonboardingValues = false;
let hasFetchedShareableValues = false;

const MINIMUM_DELAY = process.env.NODE_ENV === 'test' ? 0 : 2000;

export async function getInitialValues(
  client: ApolloClient,
  options: ValueInitializerOptions,
): Promise<InitialValues> {
  let values: InitialValues | undefined;

  if (options.type === CheckoutType.Shareable && !hasFetchedShareableValues) {
    values = await deferPromise(
      getShareableInitialValues(options),
      MINIMUM_DELAY,
    );
    hasFetchedShareableValues = true;
  }

  if (
    options.type === CheckoutType.Reonboarding &&
    !hasFetchedReonboardingValues
  ) {
    values = await deferPromise(
      getReonboardingInitialValues(options),
      MINIMUM_DELAY,
    );
    hasFetchedReonboardingValues = true;
  }

  if (options.type === CheckoutType.Standard && options.draft) {
    const { storageData } = await parseDraftToValues(client, options.draft);
    const sharedProperties = {
      checkoutType: CheckoutType.Standard,
    };
    const mergedStorageData = {
      ...storageData,
      ...sharedProperties,
    };
    values = {
      storageData:
        options.applyChallengerDefaults === true
          ? withChallengerStorageDefaults(mergedStorageData)
          : mergedStorageData,
    };
  }

  if (values) return values;

  const applyChallengerDefaults =
    options.type === CheckoutType.Standard && options.applyChallengerDefaults;

  return {
    storageData: restoreStorageSessionCheckoutData({
      applyChallengerDefaults,
    }).data,
  };
}

export { InitialValues };
